import { useEffect, useMemo, useRef, useState } from "react";
import "./ViewEmployeeModal.css";
import defaultEmployee from "../../assets/icon_employee.jpg";

const formatDate = (dateString) => {
  if (!dateString) {
    return "—";
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ViewEmployeeModal = ({
  employee,
  onClose,
  onUpdateEmployeeClick,
  handleDeleteEmployeeClick,
}) => {
  const deleteTransitionTimer = useRef(null);

  const [deleteStage, setDeleteStage] = useState("idle");

  /*
   * Reset confirmation when another employee is opened
   * and clear any pending transition timer.
   */
  useEffect(() => {
    setDeleteStage("idle");

    return () => {
      if (deleteTransitionTimer.current) {
        clearTimeout(deleteTransitionTimer.current);
      }
    };
  }, [employee?._id]);

  const vehicleGrossWeight = useMemo(() => {
    const weights = employee?.vehicle?.weightsMax;

    if (!weights) {
      return null;
    }

    const values = [
      weights.steer,
      weights.drive,
      weights.trailerA,
      weights.trailerB,
    ];

    const numericValues = values
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map(Number)
      .filter(Number.isFinite);

    if (numericValues.length === 0) {
      return null;
    }

    return numericValues.reduce((total, value) => total + value, 0);
  }, [employee]);

  const initiateDelete = () => {
    if (deleteTransitionTimer.current) {
      clearTimeout(deleteTransitionTimer.current);
    }

    setDeleteStage("transitioning");

    deleteTransitionTimer.current = setTimeout(() => {
      setDeleteStage("confirm");
    }, 300);
  };

  const cancelDelete = () => {
    if (deleteTransitionTimer.current) {
      clearTimeout(deleteTransitionTimer.current);
    }

    setDeleteStage("transitioning");

    deleteTransitionTimer.current = setTimeout(() => {
      setDeleteStage("idle");
    }, 300);
  };

  if (!employee) {
    return null;
  }

  const weightUnit = employee.vehicle?.weightsMax?.unit;

  return (
    <div className="vem-shell">
      <button
        type="button"
        className="vem-close-btn"
        onClick={onClose}
        aria-label="Close employee details"
      >
        <i className="bi bi-x-lg" />
      </button>

      <div className="vem-card">
        <div className="vem-top-row">
          <div className="vem-header">
            <div className="vem-image-wrap">
              <img
                src={employee.imageUrl || defaultEmployee}
                alt={employee.name || "Employee"}
              />
            </div>

            <div className="vem-header-info">
              <span className="vem-name">
                {employee.name || "Unnamed employee"}
              </span>

              <span className="vem-email">
                {employee.email || "No email provided"}
              </span>

              <div className="vem-meta">
                <p>
                  <span>Created</span>

                  <span className="vem-meta-value">
                    {formatDate(employee.createdAt)}
                  </span>
                </p>

                <p>
                  <span>Updated</span>

                  <span className="vem-meta-value">
                    {formatDate(employee.updatedAt)}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="vem-top-divider" />

          <div className="vem-assignment-summary">
            <span className="vem-summary-title">
              <i className="bi bi-truck" />
              Fleet Master assignment
            </span>

            <div className="vem-badge">
              <span className="vem-badge-value">{employee.employeeCode || "—"}</span>

              <span className="vem-badge-value">
                | {employee.vehicle?.vehicleCode || "Unassigned"} |
              </span>

              <span className="vem-badge-value">{employee.passcode || "—"}</span>
            </div>
          </div>
        </div>

        <div className="vem-data-area">
          <DataSection title="Assignments" icon="bi-person-badge">
            <Stat label="Registration" value={employee.vehicle?.vehicleCode} />

            <Stat
              label="Gross Weight"
              value={vehicleGrossWeight}
              unit={weightUnit}
            />
          </DataSection>

          <DataSection title="Contact" icon="bi-telephone">
            <Stat label="Phone" value={employee.phone} />

            <Stat label="Email" value={employee.email} wide />
          </DataSection>

          <DataSection title="Security" icon="bi-shield-lock">
            <Stat label="Passcode" value={employee.passcode} sensitive />
          </DataSection>
        </div>
      </div>

      <footer className={`vem-footer stage-${deleteStage}`}>
        <div className="vem-footer-inner">
          {deleteStage === "idle" && (
            <div className="vem-actions animate-in">
              <button
                type="button"
                className="vem-edit-cta"
                onClick={() => onUpdateEmployeeClick(employee)}
              >
                Edit Profile
              </button>

              <button
                type="button"
                className="vem-delete-icon"
                onClick={initiateDelete}
                aria-label="Delete employee"
              >
                <i className="bi bi-trash3" />
              </button>
            </div>
          )}

          {deleteStage === "confirm" && (
            <div className="vem-confirm animate-in">
              <p>Permanently remove this employee?</p>

              <div className="vem-confirm-btns">
                <button
                  type="button"
                  className="vem-cancel-btn"
                  onClick={cancelDelete}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="vem-delete-btn"
                  onClick={() => handleDeleteEmployeeClick(employee._id)}
                >
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

const DataSection = ({ title, icon, children }) => (
  <section className="vem-section">
    <header>
      <i className={`bi ${icon}`} />
      <span>{title}</span>
    </header>

    <div className="vem-grid">{children}</div>
  </section>
);

const Stat = ({ label, value, unit, wide = false, sensitive = false }) => {
  const hasValue = value !== null && value !== undefined && value !== "";

  return (
    <div
      className={["vem-stat", wide ? "wide" : "", sensitive ? "sensitive" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <label>{label}</label>

      <div className="vem-val-wrap">
        <span className="vem-val">{hasValue ? value : "—"}</span>

        {hasValue && unit ? <span className="vem-unit">{unit}</span> : null}
      </div>
    </div>
  );
};

export default ViewEmployeeModal;
